import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { listApiLog } from "@/api/apiLog";

const PAGE_SIZE = 50;

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("ru-RU");
}

const cellStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-subtle, #e4e7ec)",
  fontSize: 13,
} as const;

const headStyle = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-default, #d0d5dd)",
  fontSize: 12,
} as const;

type SortCol = "created_at" | "latency";

export default function AdminApiLog() {
  const [offset, setOffset] = useState(0);
  const [tokenId, setTokenId] = useState("");
  const [backend, setBackend] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<SortCol>("created_at");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const query = useQuery({
    queryKey: ["admin-api-log", { offset, tokenId, backend, status, sort, order }],
    queryFn: async () => {
      const r = await listApiLog({
        limit: PAGE_SIZE,
        offset,
        token_id: tokenId || undefined,
        backend: backend || undefined,
        status: status || undefined,
        sort,
        order,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

  const data = query.data;
  const calls = data?.calls ?? [];
  const total = data?.total ?? 0;
  const hasFilters = !!(tokenId || backend || status);

  // Смена фильтра/сортировки сбрасывает на первую страницу.
  function onSort(col: SortCol): void {
    if (sort === col) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else {
      setSort(col);
      setOrder("desc");
    }
    setOffset(0);
  }
  function sortArrow(col: SortCol): string {
    if (sort !== col) return "";
    return order === "desc" ? " ↓" : " ↑";
  }

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1100 }}>
      <header style={{ marginBottom: "var(--sp-3)" }}>
        <h1>Лог API-переводов</h1>
        <p className="mt-text-tight mt-muted">
          Каждый вызов <code>POST /v1/external/translate</code> от внешних
          клиентов. Сам текст не хранится — только sha256 + длина + метаданные.
          Обновляется каждые 30 секунд.
        </p>
      </header>

      <section className="rf-section">
        <div className="rf-section__head" style={{ gap: 8, flexWrap: "wrap" }}>
          <span>Вызовы</span>
          <select
            className="mt-input"
            style={{ maxWidth: 220 }}
            value={tokenId}
            onChange={(e) => {
              setTokenId(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">все токены</option>
            {(data?.tokens ?? []).map((t) => (
              <option key={t.token_id} value={t.token_id}>
                {t.token_name ?? t.token_id.slice(0, 8)}
              </option>
            ))}
          </select>
          <select
            className="mt-input"
            style={{ maxWidth: 160 }}
            value={backend}
            onChange={(e) => {
              setBackend(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">все бэкенды</option>
            {(data?.backends ?? []).map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <select
            className="mt-input"
            style={{ maxWidth: 130 }}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">любой статус</option>
            <option value="ok">ok</option>
            <option value="error">error</option>
          </select>
          {hasFilters && (
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--ghost"
              onClick={() => {
                setTokenId("");
                setBackend("");
                setStatus("");
                setOffset(0);
              }}
            >
              сбросить
            </button>
          )}
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          {query.isFetching && <span className="mt-spinner" />}
          <span className="mt-text-tight mt-muted">всего: {total}</span>
        </div>
        <div className="rf-section__body">
          {query.isLoading && <div className="mt-empty">Загружаем…</div>}
          {query.isError && (
            <div className="mt-empty mt-empty--error">
              {(query.error as Error | undefined)?.message ?? "Ошибка"}
            </div>
          )}
          {!query.isLoading && !query.isError && calls.length === 0 && (
            <div className="mt-empty">
              {hasFilters
                ? "Под фильтр ничего не подошло."
                : "Вызовов API пока не было."}
            </div>
          )}
          {calls.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    className="mt-muted"
                    style={{ ...headStyle, cursor: "pointer" }}
                    onClick={() => onSort("created_at")}
                    title="Сортировать по времени"
                  >
                    Время{sortArrow("created_at")}
                  </th>
                  {["Токен", "Направление", "Backend", "Символы (вх→исх)"].map(
                    (h) => (
                      <th key={h} className="mt-muted" style={headStyle}>
                        {h}
                      </th>
                    ),
                  )}
                  <th
                    className="mt-muted"
                    style={{ ...headStyle, cursor: "pointer" }}
                    onClick={() => onSort("latency")}
                    title="Сортировать по latency"
                  >
                    Latency{sortArrow("latency")}
                  </th>
                  {["Статус", "Draft"].map((h) => (
                    <th key={h} className="mt-muted" style={headStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.log_id}>
                    <td style={cellStyle}>{fmtDate(c.created_at)}</td>
                    <td style={cellStyle}>{c.token_name ?? "—"}</td>
                    <td style={cellStyle}>
                      {(c.source_lang ?? "?") + " → " + (c.target_lang ?? "?")}
                    </td>
                    <td style={cellStyle}>
                      {c.backend ?? "—"}
                      {c.backend_model ? ` (${c.backend_model})` : ""}
                    </td>
                    <td style={cellStyle}>
                      {(c.text_length ?? "—") + " → " + (c.translated_length ?? "—")}
                    </td>
                    <td style={cellStyle}>
                      {c.latency_ms != null ? `${c.latency_ms} ms` : "—"}
                    </td>
                    <td style={cellStyle}>
                      <span
                        className={
                          "mt-badge mt-badge--no-dot " +
                          (c.status === "ok"
                            ? "mt-badge--success"
                            : "mt-badge--danger")
                        }
                        title={c.error ?? undefined}
                      >
                        {c.status ?? "—"}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <span
                        className="mt-mono"
                        style={{ fontSize: 12 }}
                        title={c.draft_id ?? undefined}
                      >
                        {c.draft_id ? c.draft_id.slice(0, 8) : "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {total > 0 && (
          <div
            style={{
              padding: "8px 12px",
              borderTop: "1px solid var(--border-subtle, #e4e7ec)",
            }}
          >
            <div className="mt-pager">
              <button
                className="mt-icon-btn mt-icon-btn--bordered"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                type="button"
                aria-label="Предыдущая"
              >
                ‹
              </button>
              <span className="mt-pager__count">
                {calls.length === 0
                  ? "0"
                  : `${offset + 1} — ${offset + calls.length}`}
                {` из ${total}`}
              </span>
              <button
                className="mt-icon-btn mt-icon-btn--bordered"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + calls.length >= total}
                type="button"
                aria-label="Следующая"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
